// ⚠️ IMPORTANT: Please add MIXPANEL_PROJECT_TOKEN to your environment variables.
import { mixpanelReady } from './providers/mixpanel';

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_PROJECT_TOKEN;

const isMixpanelEnabled = !!MIXPANEL_TOKEN;

// Lazy-loaded Mixpanel instance - reuses providers/mixpanel.ts initialization (no duplicate init)
let mixpanelInstance: typeof import('mixpanel-browser').default | null = null;

/**
 * Lazy-load Mixpanel library only when needed.
 * Waits for providers/mixpanel.ts initialization to avoid duplicate init calls.
 */
async function getMixpanel(): Promise<typeof import('mixpanel-browser').default | null> {
  if (!isMixpanelEnabled) {
    return null;
  }

  if (mixpanelInstance) {
    return mixpanelInstance;
  }

  try {
    // Wait for providers/mixpanel.ts to complete initialization first
    await mixpanelReady;
    const mixpanelModule = await import('mixpanel-browser');
    mixpanelInstance = mixpanelModule.default;
  } catch (error) {
    console.error('[Mixpanel] Failed to load:', error);
  }

  return mixpanelInstance;
}

if (process.env.NODE_ENV === 'production' && !isMixpanelEnabled) {
  console.warn('Mixpanel project token is not set. Tracking will be disabled in production.');
}

export const Mixpanel = {
  identify: async (id: string) => {
    const mp = await getMixpanel();
    if (mp) {
      mp.identify(id);
    }
  },
  getDistinctId: async (): Promise<string | null> => {
    const mp = await getMixpanel();
    if (!mp) {
      return null;
    }
    try {
      return mp.get_distinct_id();
    } catch {
      return null;
    }
  },
  alias: async (id: string) => {
    const mp = await getMixpanel();
    if (mp) {
      mp.alias(id);
    }
  },
  /**
   * @deprecated Use Analytics.track() from './tracker' instead.
   * This method is kept for backward compatibility but will be removed.
   */
  track: async (_name: string, _props?: object) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[Mixpanel.track] Deprecated: Use Analytics.track() instead.', { event: _name });
    }
  },
  people: {
    set: async (props: object) => {
      const mp = await getMixpanel();
      if (mp) {
        mp.people.set(props);
      }
    },
  },
};
